import { AI_ACTION_TYPES } from '~/components/AiChat/types';
import { useCms } from '~/components/hooks/useCms';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import type { CliveConfigBooleanKey } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isProductionEnv } from '~/Utils';

type ValueOf<T> = T[keyof T];

interface ActionsFeatureFlags {
  hasDemoActions: boolean;
  actionFlagEnabledMapping: Record<ValueOf<typeof AI_ACTION_TYPES>, boolean>;
}

export const useActionsFeatureFlags = (): ActionsFeatureFlags => {
  const { userOrganization } = useCms();
  const { isCliveConfigEnabled } = useCliveClaimContext();

  const isActionsDemoEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.AI_SERVICE_ACTIONS_DEMO_DISPLAY
  );
  const isInvoiceAnalysisEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.AI_SERVICE_ACTIONS_INVOICE_ANALYSIS_DISPLAY
  );
  // Per sub-organization, from the claim's Clive configuration row, like the other Clive demo switches.
  const isDemoSwitchOn = (key: CliveConfigBooleanKey) => !isProductionEnv() && isCliveConfigEnabled(key);
  const isAcknowledgementLetterDemoEnabled = isDemoSwitchOn('demo_acknowledgement_letter_action');
  const isAdjusterAssignmentDemoEnabled = isDemoSwitchOn('demo_adjuster_assignment_action');

  return {
    hasDemoActions: isActionsDemoEnabled || isAcknowledgementLetterDemoEnabled || isAdjusterAssignmentDemoEnabled,
    actionFlagEnabledMapping: {
      [AI_ACTION_TYPES.INVOICE_ANALYSIS_RECOMMENDATION]: isInvoiceAnalysisEnabled,
      [AI_ACTION_TYPES.COMMUNICATE]: isAcknowledgementLetterDemoEnabled,
      [AI_ACTION_TYPES.ADJUSTER_ASSIGNMENT]: isAdjusterAssignmentDemoEnabled,
      [AI_ACTION_TYPES.OPEN_EXPOSURE]: isActionsDemoEnabled,
      [AI_ACTION_TYPES.SET_RESERVE]: isActionsDemoEnabled,
      [AI_ACTION_TYPES.ASSESS_DAMAGE]: isActionsDemoEnabled,
    },
  };
};
